"use client";

import { METRICS_EVENTS } from "@cede/types";
import { CEDE_CEXS } from "@cede/utils";
import { CompletedBadge } from "./CompletedBadge";
import { Button } from "@/components/ui/button";
import { useTrackEvent } from "@/hooks/data/useMetricsEvents";
import { useHubUser } from "@/hooks/user/useHubUser";
import { useOAuthFlow } from "@/hooks/user/useOAuthFlow";
import { Plug } from "lucide-react";
import { useSnackbar } from "notistack";

type ConnectOAuthProps = {
  hasCryptoComAccount: boolean;
};

export const ConnectOAuth = ({ hasCryptoComAccount }: ConnectOAuthProps) => {
  const { hubUser } = useHubUser();
  const { startOAuthFlow, isLoading } = useOAuthFlow();
  const { enqueueSnackbar } = useSnackbar();

  useTrackEvent({
    event: METRICS_EVENTS.CRYPTO_COM_OAUTH_CONNECTED,
    properties: () => ({ wallet_address: hubUser?.defiAddress ?? "" }),
    enabled: hasCryptoComAccount,
  });

  const handleConnect = async () => {
    try {
      await startOAuthFlow({
        exchangeId: CEDE_CEXS.CRYPTOCOM,
        successUrl: window.location.href,
      });
    } catch {
      enqueueSnackbar("Failed to connect Crypto.com account. Please try again.", { variant: "error" });
    }
  };

  if (hasCryptoComAccount) {
    return <CompletedBadge label="Connected" />;
  }

  return (
    <Button
      variant="primary"
      size="small"
      leftIcon={<Plug className="h-4 w-4" />}
      loading={isLoading}
      onClick={handleConnect}
    >
      {isLoading ? "Connecting..." : "Connect via OAuth"}
    </Button>
  );
};
